import { getLanguageState, subscribeLanguage } from "./language";

interface Entry {
  node: HTMLSpanElement;
  source: string;
  value: string;
  listeners: Set<() => void>;
}
const entries = new Map<string, Entry>();
let bank: HTMLDivElement | undefined;
let observer: MutationObserver | undefined;
let previousLanguage = getLanguageState().language;

function shouldExposeText() {
  const state = getLanguageState();
  return (
    state.language !== "en" ||
    (state.status === "loading" && state.requestedLanguage !== "en")
  );
}

function sourceNode(source: string) {
  const node = document.createElement("span");
  // Portal cleanup can clear aria-hidden on body siblings; keep it on each
  // source too, beyond the portal manager's scope.
  node.setAttribute("aria-hidden", "true");
  Object.assign(node.style, {
    display: "block",
    position: "absolute",
    left: "0",
    top: "0",
  });
  node.textContent = source;
  return node;
}

function translatedValue(entry: Entry) {
  if (getLanguageState().language === "en") return entry.source;
  // During a switch the observer is paused. Once it completes, never keep a
  // translation from the previous language for an unchanged English result.
  return entry.node.textContent || entry.source;
}

/**
 * Canvas and search data have no translatable DOM. Expose their English strings
 * to the same Google widget and read its output. This cache contains only Google
 * results, never hand-maintained translations or a second translation service.
 */
function getBank() {
  if (bank?.isConnected) return bank;
  bank = document.createElement("div");
  bank.setAttribute("data-google-translate-bank", "");
  bank.setAttribute("aria-hidden", "true");
  Object.assign(bank.style, {
    // Keep real boxes inside the viewport for Google's IntersectionObserver.
    // Clipping or display:none prevents newly registered strings translating.
    position: "fixed",
    left: "0",
    top: "0",
    opacity: "0",
    whiteSpace: "nowrap",
    pointerEvents: "none",
  });
  document.body.appendChild(bank);
  observer?.disconnect();
  observer = new MutationObserver(() => {
    const { language, status } = getLanguageState();
    if (status !== "ready" || document.documentElement.lang !== language)
      return;
    for (const entry of entries.values()) {
      const text = translatedValue(entry);
      if (entry.value !== text) {
        entry.value = text;
        entry.listeners.forEach((listener) => listener());
      }
    }
  });
  observer.observe(bank, {
    childList: true,
    characterData: true,
    subtree: true,
  });
  return bank;
}

export function subscribeGoogleText(
  sources: readonly string[],
  listener: () => void,
): () => void {
  const used: Entry[] = [];
  for (const source of new Set(sources.filter((text) => text.trim()))) {
    let entry = entries.get(source);
    if (!entry) {
      const node = sourceNode(source);
      if (shouldExposeText()) {
        getBank().appendChild(node);
      }
      entry = { source, value: source, node, listeners: new Set() };
      entries.set(source, entry);
    } else if (!entry.node.isConnected && shouldExposeText()) {
      getBank().appendChild(entry.node);
    }
    entry.listeners.add(listener);
    used.push(entry);
  }
  return () => {
    for (const entry of used) {
      entry.listeners.delete(listener);
      if (entry.listeners.size === 0) {
        entry.node.remove();
        entries.delete(entry.source);
      }
    }
    if (entries.size === 0) {
      observer?.disconnect();
      bank?.remove();
      bank = undefined;
    }
  };
}

export function getGoogleText(source: string): string {
  return getLanguageState().language === "en"
    ? source
    : entries.get(source)?.value || source;
}

/** Call only after Google has restored the document to English. Its restore
 * cache can replace bank nodes, so each new target must see fresh sources. */
export function resetGoogleTextSources() {
  observer?.disconnect();
  bank?.remove();
  bank = undefined;
  for (const entry of entries.values()) {
    entry.node = sourceNode(entry.source);
    if (shouldExposeText()) getBank().appendChild(entry.node);
  }
}

const unsubscribeLanguage = subscribeLanguage(() => {
  const { language, status } = getLanguageState();
  if (shouldExposeText()) {
    for (const entry of entries.values())
      if (!entry.node.isConnected) {
        getBank().appendChild(entry.node);
      }
  }
  if (status !== "ready") return;
  const changed = previousLanguage !== language;
  previousLanguage = language;
  for (const entry of entries.values()) {
    const value = translatedValue(entry);
    if (entry.value !== value || changed) {
      entry.value = value;
      entry.listeners.forEach((listener) => listener());
    }
  }
  if (language === "en") {
    resetGoogleTextSources();
  }
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unsubscribeLanguage();
    observer?.disconnect();
    bank?.remove();
    entries.clear();
  });
}
